/* eslint-disable react/no-unescaped-entities */

import { motion } from "framer-motion";
import { Check, Star } from "lucide-react";

const ONLINE = [
  "Accès à tous les modules vidéo",
  "Accès illimité à vie",
  "Formation à votre rythme",
  "Méthodes de lecture & écriture ECU",
  "Conversion E85",
  "Préparations Stage 1",
  "Préparations Stage 2",
  "Communication digitale (Meta Ads)",
  "Google Business Profile",
  "Développement commercial",
  "Support pédagogique en ligne",
  "Groupe privé entraide",
];

const PREMIUM = [
  "Tout le contenu de la formation en ligne",
  "3 jours intensifs dans un centre Horsepower",
  "Pratique sur véhicules clients réels",
  "Lecture & écriture ECU en conditions réelles",
  "Utilisation des outils professionnels",
  "Passage sur banc de puissance",
  "Diagnostic avancé encadré",
  "Accompagnement lancement d'activité",
  "Questions / réponses en direct",
  "Support privilégié WhatsApp après formation",
  "Sessions live mensuelles",
  "Accès File Service Horsepower",
];

export default function Pricing({ onCtaClick }) {
  return (
    <section className="relative py-16 md:py-24 lg:py-32 bg-[#050505]" id="formules" data-testid="pricing-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <div className="max-w-3xl mb-16">
          <div className="flex items-center gap-3 mb-6">
            <span className="h-px w-12 bg-[#FFD60A]" />
            <span className="font-mono text-xs tracking-[0.4em] text-[#FFD60A] uppercase">Tarification</span>
          </div>
          <h2 className="font-display text-4xl md:text-5xl lg:text-6xl text-white uppercase leading-[0.95]">
            Choisissez votre <span className="text-[#FFD60A]">formule</span>
          </h2>
          <p className="mt-6 text-lg text-zinc-400">
            Deux formats pensés pour répondre à votre niveau d'engagement et à votre budget.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-6 lg:gap-8">
          {/* Online formula */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="relative bg-[#0a0a0a] border border-zinc-800 p-8 md:p-10 flex flex-col"
            data-testid="pricing-card-online"
          >
            <div className="font-mono text-xs tracking-[0.3em] text-zinc-500 uppercase mb-3">Formule 01</div>
            <h3 className="font-display text-4xl md:text-5xl text-white uppercase leading-none">
              Formation en ligne
            </h3>
            <p className="mt-4 text-zinc-400">
              L'autonomie totale. Tout le contenu pédagogique en accès illimité, à votre rythme, où vous voulez.
            </p>

            <div className="mt-8 flex items-baseline gap-3">
              <span className="font-display text-6xl text-white tracking-tight">990<span className="text-3xl align-top text-zinc-500">€</span></span>
              <span className="text-xs text-zinc-500 uppercase tracking-widest">TTC</span>
            </div>
            <div className="mt-2 inline-flex items-center gap-2 px-3 py-1 border border-[#FFD60A]/40 bg-[#FFD60A]/10 text-[#FFD60A] font-mono text-[10px] uppercase tracking-widest w-fit">
              Ou 4 × 247,50€ sans frais
            </div>

            <div className="my-8 h-px bg-zinc-800" />

            <ul className="space-y-3 flex-1">
              {ONLINE.map((item, i) => (
                <li key={i} className="flex items-start gap-3 text-sm text-zinc-300">
                  <Check size={16} className="text-[#FFD60A] mt-0.5 shrink-0" strokeWidth={2.5} />
                  <span>{item}</span>
                </li>
              ))}
            </ul>

            <button
              onClick={() => onCtaClick("call_booking", { plan: "online", campaign: "cta-pricing-online" })}
              className="mt-10 w-full py-4 border border-white text-white font-bold uppercase tracking-wider hover:bg-white hover:text-black transition-all"
              data-testid="pricing-cta-online"
            >
              Je choisis la formation en ligne
            </button>
          </motion.div>

          {/* Premium formula */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.15 }}
            className="relative bg-[#0a0a0a] border-2 border-[#FFD60A] p-8 md:p-10 flex flex-col shadow-[0_0_60px_-15px_rgba(255,214,10,0.4)]"
            data-testid="pricing-card-premium"
          >
            <div className="absolute -top-3 left-8 px-4 py-1.5 bg-[#FFD60A] text-black font-bold text-xs uppercase tracking-widest flex items-center gap-1.5">
              <Star size={12} fill="currentColor" />
              Recommandée
            </div>

            <div className="font-mono text-xs tracking-[0.3em] text-[#FFD60A] uppercase mb-3">Formule 02</div>
            <h3 className="font-display text-4xl md:text-5xl text-white uppercase leading-none">
              Premium <span className="text-[#FFD60A]">Présentielle</span>
            </h3>
            <p className="mt-4 text-zinc-300">
              L'expérience complète. 3 jours sur le terrain dans un centre Horsepower + tout le contenu en ligne à vie.
            </p>

            <div className="mt-8 flex items-baseline gap-3">
              <span className="font-display text-6xl text-[#FFD60A] tracking-tight">1990<span className="text-3xl align-top text-zinc-400">€</span></span>
              <span className="text-xs text-zinc-400 uppercase tracking-widest">TTC</span>
            </div>
            <div className="mt-2 inline-flex items-center gap-2 px-3 py-1 border border-[#FFD60A]/60 bg-[#FFD60A]/15 text-[#FFD60A] font-mono text-[10px] uppercase tracking-widest w-fit">
              Ou 4 × 497,50€ sans frais
            </div>

            <div className="my-8 h-px bg-zinc-800" />

            <ul className="space-y-3 flex-1">
              {PREMIUM.map((item, i) => (
                <li key={i} className="flex items-start gap-3 text-sm text-zinc-200">
                  <Check size={16} className="text-[#FFD60A] mt-0.5 shrink-0" strokeWidth={2.5} />
                  <span>{item}</span>
                </li>
              ))}
            </ul>

            <button
              onClick={() => onCtaClick("call_booking", { plan: "premium", campaign: "cta-pricing-premium" })}
              className="mt-10 w-full py-4 bg-[#FFD60A] text-black font-bold uppercase tracking-wider hover:bg-yellow-300 transition-all hover:-translate-y-0.5"
              data-testid="pricing-cta-premium"
            >
              Réserver ma place
            </button>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
